import React, { useEffect, useState } from 'react';
import { toast } from 'react-toastify';

import { ApolloClient, DocumentNode, useApolloClient } from '@apollo/client';
import { IDynamicFormWidgetSection } from '@components/dynamic-form/types';
import { normalizeDynamicFormDrawerValues } from '@components/dynamic-form/utils';
import ToastErrorMessage from '@components/toast/ToastErrorMessage';
import ToastSuccessMessage from '@components/toast/ToastSuccessMessage';
import {
  enumListAsRecordObject,
  lookupListAsRecordObject
} from '@utils/graph-utils';

import DateService from 'src/core/services/DateService';
import { downloadExcelFile } from '@utils/excel/download-json-excel-service';
import DynamicFormWidget from '@components/dynamic-form/DynamicFormWidget';
import {
  GetReinsurersResponse,
  GetReinsurersVariables,
  IBaseDrawerProps
} from '@common/types';
import { capitalizeFirstLetter } from '@utils/formatting-utils';
import FormattingService from '@core/services/FormattingService';
import {
  ALL,
  exportReinsurersDrawerSections,
  exportReinsurersExcelColumnWidths,
  exportReinsurersExcelHeaderGroups,
  exportReinsurersExcelHeaders,
  exportReinsurersExcelTemplateStyles,
  IExportReinsurersFilters
} from '@common/constants';
import { useGetReinsurerExportLovs, useGetReinsurers } from '@common/slices';
import { useSelector } from 'react-redux';
import { RootState } from '@core/redux/store';

export const normalizeMulti = (v: any): string[] => {
  if (!v) return [];

  return Array.isArray(v) ? v : [v];
};

export const formatForFileName = (d: any) =>
  DateService.formatDate(d).replaceAll('/', '-');

export const normalizeStatusesForBackend = (values: string[] | null) => {
  if (!values?.length) return null;

  return values.map((s) => capitalizeFirstLetter(String(s).toLowerCase()));
};

export const normalizeIsLocalForBackend = (values: string[] | null) => {
  if (!values?.length) return null;

  const hasYes = values.includes('Yes');
  const hasNo = values.includes('No');

  if (hasYes && hasNo) return null;
  if (hasYes) return true;
  if (hasNo) return false;

  return null;
};

export const fetchAllReinsurers = async (
  getReisurers: (options?: {
    variables: GetReinsurersVariables;
  }) => Promise<{ data: GetReinsurersResponse }>,
  vars: {
    companies: string[] | null;
    statuses: string[] | null;
    isLocal: boolean | null;
    createdDateFrom: any | null;
    createdDateTo: any | null;
  }
) => {
  const pageSize = 500;
  let pageNumber = 1;

  const all: any[] = [];
  let hasMore = true;

  while (hasMore) {
    const { data } = await getReisurers({
      variables: {
        pagination: { pageSize, pageNumber },
        companies: vars.companies,
        statuses: vars.statuses,
        isLocal: vars.isLocal,
        createdDateFrom: vars.createdDateFrom,
        createdDateTo: vars.createdDateTo,
        keyword: null,
        orderBy: null
      }
    });

    const items = data?.reinsurance?.searchReinsurers?.items || [];
    all.push(...items);

    hasMore = items.length === pageSize;
    pageNumber += 1;
  }

  return all;
};

const boolToYesNo = (v: any) => {
  if (v === true) return 'Yes';
  if (v === false) return 'No';

  return '';
};

export const mapItemsToExcelData = (items: any[]) => {
  return (items ?? [])
    .map((bp: any) => ({
      code: bp?.code ?? '',
      fullName: bp?.fullName ?? '',
      isCompany: boolToYesNo(bp?.isCompany),
      isLocal: boolToYesNo(bp?.isLocal),
      isRegistered: boolToYesNo(bp?.isRegistered),
      registrationNumber: bp?.registerationNumber ?? '',
      registrationExpiryDate: bp?.registerationExpiryDate ?? '',
      taxAuxiliaryAccount: bp?.taxAuxiliaryAccount ?? '',
      auxiliaryAccount: bp?.auxiliaryAccount ?? '',
      status:
        FormattingService.formatEnumToDisplay(bp?.status) ?? bp?.status ?? ''
    }))
    .sort((a, b) => String(a.code || '').localeCompare(String(b.code || '')));
};

const withAll = (options: Record<string, string>) => ({
  [ALL]: 'All',
  ...(options ?? {})
});

const ExportReinsurersDrawer: React.FC<IBaseDrawerProps> = ({ onSuccess }) => {
  const client = useApolloClient();
  const submitButtonState = useSelector(
    (state: RootState) => state.apiState.buttonState
  );

  const [values, setValues] = useState<IExportReinsurersFilters>({
    relatedCompany: [ALL],
    isLocal: [ALL],
    status: [ALL],
    createdOn: []
  });

  const [lovs, setLovs] = useState<Record<string, Record<string, string>>>({});
  const [sections, setSections] = useState<IDynamicFormWidgetSection[]>([]);

  const { data: lovData } = useGetReinsurerExportLovs.useQuery();
  const [getReinsurers] = useGetReinsurers.useLazy();
  useEffect(() => {
    if (!lovData) return;

    const relatedCompanyOptions = withAll(
      lookupListAsRecordObject(lovData?.salesforce?.getRelatedCompanies || [])
    );

    const statusOptions = withAll(
      enumListAsRecordObject(
        lovData?.BusinessPartnerStatusList?.enumValues || []
      )
    );

    const yesNoOptions = { [ALL]: 'All', Yes: 'Yes', No: 'No' };

    const built = {
      RelatedCompanyList: relatedCompanyOptions,
      BusinessPartnerStatusList: statusOptions,
      YesNoList: yesNoOptions
    };

    setLovs(built);
    setSections(exportReinsurersDrawerSections(values, built));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [lovData]);

  const submitForm = async (formValues: Record<string, any>) => {
    const [data] = normalizeDynamicFormDrawerValues(sections, formValues);

    const selectedStatuses = normalizeMulti(data.status);
    const selectedIsLocal = normalizeMulti(data.isLocal);

    if (!selectedStatuses.length || !selectedIsLocal.length) {
      toast.error(
        <ToastErrorMessage>Required fields are missing</ToastErrorMessage>
      );
    }

    const statusesVar = selectedStatuses.includes(ALL)
      ? null
      : normalizeStatusesForBackend(selectedStatuses);

    const isLocalVar = selectedIsLocal.includes(ALL)
      ? null
      : normalizeIsLocalForBackend(selectedIsLocal);

    const formattedDateRange = DateService.formatDateTimeRangeBackend(
      data.createdOn
    );
    const createdDateFrom = formattedDateRange?.[0] || null;
    const createdDateTo = formattedDateRange?.[1] || null;

    const pickerFrom = data.createdOn?.[0];
    const pickerTo = data.createdOn?.[1];

    let fileName = 'Redbridge_Reinsurers';
    if (pickerFrom && pickerTo) {
      fileName += `_${formatForFileName(pickerFrom)}_${formatForFileName(pickerTo)}`;
    }

    const reinsurers = await fetchAllReinsurers(getReinsurers, {
      companies: null,
      statuses: statusesVar,
      isLocal: isLocalVar,
      createdDateFrom,
      createdDateTo
    });

    if (!reinsurers.length) {
      toast.error(
        <ToastErrorMessage>
          No reinsurers found for the selected filters
        </ToastErrorMessage>
      );
      return;
    }
    const excelData = mapItemsToExcelData(reinsurers);

    downloadExcelFile({
      data: excelData,
      filename: fileName,
      headers: exportReinsurersExcelHeaders,
      sheetName: 'Sheet2',
      groupHeaders: exportReinsurersExcelHeaderGroups,
      styles: exportReinsurersExcelTemplateStyles,
      columnWidths: exportReinsurersExcelColumnWidths,
      freezeHeaderRows: 0
    });
    onSuccess();
  };

  const handleChange = (
    fieldName: keyof IExportReinsurersFilters,
    value: any,
    sectionIndex: number,
    updatedValues: IExportReinsurersFilters
  ) => {
    const newValues = { ...updatedValues, [fieldName]: value };
    setValues(newValues);
    setSections(exportReinsurersDrawerSections(newValues, lovs));
  };

  return (
    <DynamicFormWidget
      onChange={handleChange}
      sections={sections}
      submitButtonState={submitButtonState}
      onSubmit={submitForm}
    />
  );
};

export { ExportReinsurersDrawer };
